import type { ExtendedBid } from '@/features/bidding/types/bid'
import { logMessageInCoralogix } from '@/features/coralogix/utils/sendExceptions'

type ComparisonResult =
  | { areEqual: false; result: { frontend: string; backend: string } }
  | { areEqual: true; result: null }

/**
 * Normalize ExtendedBid for comparison by:
 * - Sorting arrays for consistent ordering
 * - Extracting only the fields we care about (ptuChunks)
 */
const normalizeForComparison = (bids: ExtendedBid[]) => {
  return bids
    .map((bid) => ({
      activationGroupUuid: bid.activationGroupUuid,
      ptus: bid.ptus.map((ptu) => ({
        ptuStart: ptu.ptu.start.toISO(),
        ptuChunks: (ptu.ptuChunks || [])
          .map((chunk) => ({
            price: chunk.offeredPrice,
            quantity: chunk.offeredVolume.quantity,
          }))
          .sort((a, b) => a.price - b.price),
      })),
    }))
    .sort((a, b) => a.activationGroupUuid.localeCompare(b.activationGroupUuid))
}

/**
 * Compare two ExtendedBid arrays using JSON serialization
 */
export const comparePriceAllocations = (
  frontendResult: ExtendedBid[],
  backendResult: ExtendedBid[],
): ComparisonResult => {
  const normalizedFrontend = normalizeForComparison(frontendResult)
  const normalizedBackend = normalizeForComparison(backendResult)

  const frontendJson = JSON.stringify(normalizedFrontend, null, 2)
  const backendJson = JSON.stringify(normalizedBackend, null, 2)

  if (frontendJson === backendJson) {
    return { areEqual: true, result: null }
  } else {
    return {
      areEqual: false,
      result: {
        frontend: frontendJson,
        backend: backendJson,
      },
    }
  }
}

/**
 * Logs price allocation comparison results with detailed error information
 */
export const logPriceAllocationComparison = (comparisonResult: ComparisonResult) => {
  if (comparisonResult.areEqual) {
    return
  }
  logMessageInCoralogix(
    '[Price Allocation Shadow] MISMATCH DETECTED between frontend and backend allocations',
    JSON.stringify(comparisonResult.result),
  )
}
